import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@eo/shared';

import { barPercent } from './helpers';
import type { LoadBarProps } from './types';

// A bullet bar: filled segments against a rail, a solid tick for the target
// and a dotted tick for where the fill should be today. Every proportion comes
// from the values passed in; the label carries the same reading in words, and
// each tick says what it marks when pointed at.
export function LoadBar({
  segments,
  scale,
  target,
  pace,
  label,
  targetHint,
  paceHint,
  details,
}: LoadBarProps) {
  const tick = (kind: 'target' | 'pace', hours: number, hint: string | undefined, line: string) => {
    const mark = <span className={cn('h-full', line)} />;
    // The hit area is wider than the 2px line, so it can be pointed at.
    const area = 'absolute -inset-y-1 flex w-3 -translate-x-1/2 justify-center';
    return hint ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`load-bar-${kind}`}
            tabIndex={-1}
            // An empty title keeps the bar's own reading from also popping up here.
            title=""
            className={cn(area, 'cursor-help')}
            style={{ left: barPercent(hours, scale) }}
          >
            {mark}
          </span>
        </TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    ) : (
      <span className={area} style={{ left: barPercent(hours, scale) }}>
        {mark}
      </span>
    );
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div
        role="img"
        aria-label={label}
        // A bar that lists its own breakdown on point needs no browser tooltip too.
        title={details ? undefined : label}
        className="relative h-3 w-full rounded-[4px] bg-capacity-rail"
      >
        {details ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                data-testid="load-bar-body"
                tabIndex={-1}
                className="absolute inset-y-0 left-0 flex w-full cursor-help gap-[2px]"
              >
                {segments
                  .filter((s) => s.hours > 0)
                  .map((s) => (
                    <div
                      key={s.key}
                      className={cn(
                        'h-full first:rounded-l-[4px] last:rounded-r-[4px]',
                        s.className,
                      )}
                      style={{ width: barPercent(s.hours, scale) }}
                    />
                  ))}
              </div>
            </TooltipTrigger>
            <TooltipContent>{details}</TooltipContent>
          </Tooltip>
        ) : (
          <div className="absolute inset-y-0 left-0 flex w-full gap-[2px]">
            {segments
              .filter((s) => s.hours > 0)
              .map((s) => (
                <div
                  key={s.key}
                  className={cn('h-full first:rounded-l-[4px] last:rounded-r-[4px]', s.className)}
                  style={{ width: barPercent(s.hours, scale) }}
                />
              ))}
          </div>
        )}
        {target !== undefined &&
          target > 0 &&
          tick('target', target, targetHint, 'w-[2px] bg-text-primary')}
        {pace !== undefined &&
          pace > 0 &&
          tick('pace', pace, paceHint, 'border-l-2 border-dotted border-text-tertiary')}
      </div>
    </TooltipProvider>
  );
}
